import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Music2, Code2, Settings, ArrowRight, Wifi } from 'lucide-react';
import { BytebeatLayout } from '@/components/BytebeatLayout';
import { Button } from '@/components/ui/button';

const SAMPLE_FRAGMENT = btoa('Classic Bytebeat|8000|t*(t>>9|t>>13)&25|t*(t>>12|t>>16)&63')
  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const Index = () => {
  useSeoMeta({
    title: 'Bytebeat Player — Algorithmic Music in a URL',
    description: 'Create, play, and share bytebeat compositions encoded entirely in a URL. Works offline as a PWA.',
  });

  return (
    <BytebeatLayout>
      <div className="flex flex-col items-center justify-center py-12 text-center gap-10">
        {/* Hero */}
        <div className="space-y-4 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-xs text-primary/80 mb-2">
            <Wifi className="w-3 h-3" />
            Works 100% offline — no server, no database
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
            <span className="text-primary glow-cyan">Bytebeat</span>{' '}
            <span className="text-foreground/90">Player</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Algorithmic music packed entirely into a URL. Every link is a complete,
            self-contained composition — no files, no uploads.
          </p>

          <div className="flex flex-wrap gap-3 justify-center pt-2">
            <Link to="/editor">
              <Button size="lg" className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground">
                <Code2 className="w-4 h-4" />
                Open Editor
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to={`/player#${SAMPLE_FRAGMENT}`}>
              <Button size="lg" variant="outline" className="gap-2 border-border/60 hover:border-accent/40">
                <Music2 className="w-4 h-4" />
                Try a Sample
              </Button>
            </Link>
          </div>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-3xl mt-4">
          <FeatureCard
            icon={<Music2 className="w-5 h-5 text-primary" />}
            title="Player"
            description="Play any bytebeat shared via URL. Generates a unique cover art from the code itself."
            href="/player"
          />
          <FeatureCard
            icon={<Code2 className="w-5 h-5 text-accent" />}
            title="Editor"
            description="Write bytebeat expressions, set variables, and instantly generate a shareable link."
            href="/editor"
          />
          <FeatureCard
            icon={<Settings className="w-5 h-5 text-muted-foreground" />}
            title="Settings"
            description="Manage the PWA installation and offline caching for use without internet."
            href="/settings"
          />
        </div>

        {/* How it works */}
        <div className="max-w-2xl w-full space-y-3 text-left">
          <h2 className="text-lg font-semibold text-foreground/80 text-center">How it works</h2>
          <div className="bg-card/60 border border-border/40 rounded-xl p-4 space-y-3 text-sm text-muted-foreground">
            <Step n={1} text="Write a bytebeat expression using t (time counter) in the Editor." />
            <Step n={2} text="The editor encodes your code, name, bitrate, and arguments into a base64url URL fragment." />
            <Step n={3} text="Share the link — the recipient opens /player#<fragment> and it plays instantly." />
            <Step n={4} text="A verification phrase derived from the fragment lets both parties confirm the link is intact." />
          </div>
        </div>

        <p className="text-xs text-muted-foreground/40">
          <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="hover:text-muted-foreground/60 transition-colors">
            Vibed with Shakespeare
          </a>
        </p>
      </div>
    </BytebeatLayout>
  );
};

function FeatureCard({ icon, title, description, href }: {
  icon: React.ReactNode;
  title: string;
  description: string;
  href: string;
}) {
  return (
    <Link to={href} className="group">
      <div className="h-full p-4 rounded-xl border border-border/40 bg-card/60 hover:border-primary/30 hover:bg-card/80 transition-all duration-200 text-left space-y-2">
        <div className="w-9 h-9 rounded-lg bg-muted/60 flex items-center justify-center group-hover:bg-muted/80 transition-colors">
          {icon}
        </div>
        <h3 className="font-semibold text-sm">{title}</h3>
        <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
      </div>
    </Link>
  );
}

function Step({ n, text }: { n: number; text: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-5 h-5 rounded-full bg-primary/20 border border-primary/30 text-primary text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-bold">
        {n}
      </span>
      <span>{text}</span>
    </div>
  );
}

export default Index;
